import { ReactNode, useState } from "react";
import { Row } from "../Row";
import { Col } from "../Col";
import clsx from "clsx";
import { Link } from "@anoki/fse-common";
import { ActionIconText } from "@/components";
import { ArrowRight, Download } from "@/icons";
import { saveAs } from "file-saver";

type OperatorCardTableItem = {
  label: string;
  value: ReactNode;
};

type BaseProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  items: OperatorCardTableItem[];
  className?: string;
  disabled?: boolean;
};

type ExpandableProps = BaseProps & {
  variant: "expandable";
  children?: ReactNode;
  actionLabel?: {
    moreInfo?: string;
    lessInfo?: string;
  };
  onAction?: () => void;
};

type LinkProps = BaseProps & {
  variant: "link";
  link: Link;
  children?: never;
};

type OnActionsProps = BaseProps & {
  variant: "onAction";
  onAction: () => void;
  onActionLabel?: string;
  children?: never;
};

type LabelProps = BaseProps & {
  variant: "label";
  children?: never;
  bottomLabel: string;
};

type Props = ExpandableProps | LinkProps | OnActionsProps | LabelProps;

export const OperatorCardTable = (props: Props) => {
  const { title, subtitle, items, className, disabled } = props;

  const [isExpanded, setIsExpanded] = useState(false);

  const isLink = props.variant === "link";
  const isOnAction = props.variant === "onAction";
  const isLabel = props.variant === "label";
  const isExpandable = props.variant === "expandable";
  // Default labels for expandable variant
  const expandLabels =
    isExpandable
      ? {
        moreInfo: props.actionLabel?.moreInfo ?? "Più informazioni",
        lessInfo: props.actionLabel?.lessInfo ?? "Meno informazioni",
      }
      : undefined;

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleDownload = async () => {
    if (isLink) {
      try {
        const response = await fetch(props.link.link);
        const blob = await response.blob();
        saveAs(blob, props.link.title);
      } catch (error) {
        console.error("Error downloading file:", error);
      }
    }
  };

  return (
    <Col className={clsx("bg-white br-x8 shadow", className)} gap="x8">
      <Row justifyContent="space-between" px="x16" pt="x16">
        <Col className="w-100">
          <h3 className="fs-5 fw-bold text-primary mb-x8">{title}</h3>
          {subtitle && (
            <div className="d-flex flex-column gap-x4 mb-x16">{subtitle}</div>
          )}
          {isLabel ? (
            <Col gap="x16">
              {items.map((item, index) => (
                <Col key={index}>
                  <div className="text-muted fs-6">{item.label}</div>
                  <div className="fw-semibold fs-6 text-grey-black">
                    {item.value}
                  </div>
                </Col>
              ))}
            </Col>
          ) : (
            <Row justifyContent="space-between" flexWrap="wrap">
              {items.map((item, index) => (
                <Col key={index}>
                  <div className="text-muted fs-6">{item.label}</div>
                  <div className="fw-semibold fs-6 text-grey-black">
                    {item.value}
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </Col>
      </Row>
      <Row justifyContent={isExpandable ? "flex-end" : "center"} bg="lightblue-a03" p="x8">
        {!isLabel ? (
          <ActionIconText
            textLabel={
              isOnAction
                ? (props.onActionLabel ?? "Scarica documento (PDF • X MB)")
                : isLink
                  ? props.link.title
                  : expandLabels?.moreInfo
            }
            iconColor="slate-20"
            iconPosition={isOnAction || isLink ? "left" : "right"}
            disablePointer={disabled}
            handleAction={
              isOnAction
                ? props.onAction
                : isLink
                  ? handleDownload
                  : (props.onAction ?? handleExpandToggle)
            }
            icon={
              isOnAction || isLink ? (
                <Download />
              ) : (
                <ArrowRight
                  className={`cursor-pointer`}
                />
              )
            }
          />
        ) : (
          <p className="text-center text-black fs-6">{props.bottomLabel}</p>
        )}
      </Row>
      {/* isExpanded && props.children && props.variant === "expandable" && (
        <Col className="mt-x16 pt-x16 border-top">{props.children}</Col>
      ) */}
    </Col >
  );
};
